import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { ThemeStill } from '../themes/ThemeStill';
import { releaseThemeMessages } from '../themes/releaseThemeMessages';
import { useKitWebFonts } from '../themes/kitWebFonts';
import { RELEASE_ARTWORK_PX, releaseArtworkUrl } from './aiReel/releaseArtworkUrl';
import { StudioThemeDialog } from './StudioThemeDialog';
import { SongTheme } from './useSongTheme';
import { studioMessages } from './studioMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the theme chip and its dialog */
import styles from './StudioTheme.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StudioThemeChipProps {
  accountId: number;
  item: CatalogItem;
  songTheme: SongTheme;
}

/**
 * The theme of the song in the Studio song bar (design q3a, q4b): a 26 px mini still, the label
 * "Theme", the name and "Change". "Change" opens the Theme panel in a dialog.
 */
const StudioThemeChipComponent: React.FC<StudioThemeChipProps> = ({ accountId, item, songTheme }) => {
  const intl = useIntl();
  useKitWebFonts();
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const { look, palette, kit } = songTheme;
  const themeName = intl.formatMessage(releaseThemeMessages[look.themeId].name);

  return (
    <span className={styles.chip} data-testid="studio-theme-chip">
      <span className={styles.mini}>
        <ThemeStill
          themeId={look.themeId}
          fontOverride={look.fontOverride}
          palette={palette}
          artworkUrl={releaseArtworkUrl(item, RELEASE_ARTWORK_PX.thumb)}
          title={item.title.slice(0, 1)}
          artist=""
          kicker=""
          mini
        />
      </span>
      <span className={styles.label}>{intl.formatMessage(studioMessages.themeChipLabel)}</span>
      <b className={styles.name}>{themeName}</b>
      <button
        type="button"
        className={styles.change}
        aria-label={intl.formatMessage(studioMessages.themeChipAria, { title: item.title })}
        disabled={kit === undefined}
        onClick={open}
      >
        {intl.formatMessage(studioMessages.themeChipChange)}
      </button>
      {kit !== undefined && (
        <StudioThemeDialog isOpen={isOpen} accountId={accountId} item={item} kit={kit} onClose={close} />
      )}
    </span>
  );
};

export const StudioThemeChip = TypedMemo(StudioThemeChipComponent);
